import React, { useContext, useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { useTranslation } from "react-i18next";
import { LinearProgress } from "@mui/material";
import Navigation from "./Navigation";
import Details from "./Details";
import Intervals from "./Intervals";
import Attachments from "./Attachments";
import Stages from "./Stages";
import Zones from "./Zones";
import Parts from "./Parts";
import Lubricant from "./Lubricant";
import Tools from "./Tools";
import Permits from "./Permits";
import WorkBook from "./WorkBook";
import Images from "./Images";
import Questions from "./Questions";
import { TaskContext } from "contexts/TaskDetailContext";
import { useLocation } from "react-router-dom";
import Arrangements from "./Arrangements";
import useSiteApplicationDetail from "hooks/useSiteApplicationDetail";

export const useStyles = makeStyles()((theme) => ({
	main: {
		background: "#FFFFFF",
		marginBottom: 22,
		paddingBottom: 12,
		display: "flex",
		flexDirection: "column",
		minHeight: "150px",
	},
	component: { width: "95%", margin: "auto" },
}));

const ModelTaskExpand = ({
	state,
	taskInfo,
	taskLoading,
	access,
	originalRow,
	setTaskInfo,
}) => {
	const location = useLocation();
	const { classes } = useStyles();
	const { t } = useTranslation();
	const [current, setCurrent] = useState("Details");
	const {
		application: { showParts, showArrangements, showLubricants },
		customCaptions,
	} =
		JSON.parse(sessionStorage.getItem("me")) ||
		JSON.parse(localStorage.getItem("me"));

	const [TaskDetailState, taskDispatch] = useContext(TaskContext);

	const { taskInfo: TaskDetail, stageList } = TaskDetailState;

	//current site id
	const { siteAppID } =
		JSON.parse(sessionStorage.getItem("me")) ||
		JSON.parse(localStorage.getItem("me"));

	const siteAppState = useSiteApplicationDetail(siteAppID);

	useEffect(() => {
		const isTaskWithHighlightedQuestion =
			location.state?.modelVersionTaskID === taskInfo.id;
		if (
			isTaskWithHighlightedQuestion &&
			location.state?.modelVersionQuestionID &&
			!taskLoading
		)
			setCurrent("question");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [taskLoading]);

	useEffect(() => {
		if (TaskDetail?.intervalCount === 0 || 0) {
			taskDispatch({
				type: "SET_TASK_ERROR",
				payload: {
					name: "interval",
					value: t("message.noEntityAssigned", { entity: customCaptions?.intervalPlural }),
				},
			});
		} else {
			taskDispatch({
				type: "SET_TASK_ERROR",
				payload: {
					name: "interval",
					value: "",
				},
			});
		}
		if (TaskDetail?.stageCount === 0 || 0) {
			taskDispatch({
				type: "SET_TASK_ERROR",
				payload: {
					name: "stage",
					value: t("message.noEntityAssigned", { entity: customCaptions?.stagePlural }),
				},
			});
		} else {
			taskDispatch({
				type: "SET_TASK_ERROR",
				payload: {
					name: "stage",
					value: "",
				},
			});
		}
		if (
			stageList.filter((x) => x.hasZones && x.id !== null)?.length > 0 &&
			TaskDetail?.zoneCount === 0 &&
			TaskDetail?.stageCount !== 0
		) {
			taskDispatch({
				type: "SET_TASK_ERROR",
				payload: {
					name: "zone",
					value: t("message.noEntityAssigned", { entity: customCaptions?.zonePlural }),
				},
			});
		} else {
			taskDispatch({
				type: "SET_TASK_ERROR",
				payload: {
					name: "zone",
					value: "",
				},
			});
		}
		if (
			TaskDetail?.roles === null ||
			TaskDetail?.roles?.filter((r) => r.id !== null).length === 0
		) {
			taskDispatch({
				type: "SET_TASK_ERROR",
				payload: {
					name: "role",
					value: t("message.noEntityAssigned", { entity: customCaptions?.rolePlural }),
				},
			});
		} else {
			taskDispatch({
				type: "SET_TASK_ERROR",
				payload: {
					name: "role",
					value: "",
				},
			});
		}
		if (+TaskDetail.estimatedMinutes <= 0) {
			taskDispatch({
				type: "SET_TASK_ERROR",
				payload: {
					name: "estimatedMinutes",
					value: t("message.invalidEstimatedMinutes"),
				},
			});
		} else {
			taskDispatch({
				type: "SET_TASK_ERROR",
				payload: {
					name: "estimatedMinutes",
					value: "",
				},
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		TaskDetail.intervalCount,
		taskDispatch,
		customCaptions.intervalPlural,
		customCaptions.stagePlural,
		TaskDetail.stageCount,
		TaskDetail.zoneCount,
		TaskDetail.estimatedMinutes,
		TaskDetail.roles,
		originalRow.stages,
		stageList,
	]);

	// The reducer's hasTools/hasPermits/etc fields drive the row's icon tray,
	// which renders them as booleans (`{hasTools && <Icon/>}`) - a raw count of
	// 0 is falsy but still a number, and React renders a falsy number as literal
	// text instead of nothing. Coerce every count to a boolean before dispatching.
	useEffect(() => {
		taskDispatch({ type: "SET_TOOLS", payload: Boolean(TaskDetail.toolCount) });
		taskDispatch({ type: "SET_PERMITS", payload: Boolean(TaskDetail.permitCount) });
		taskDispatch({ type: "SET_IMAGES", payload: Boolean(TaskDetail.imageCount) });
		taskDispatch({ type: "SET_QUESTIONS", payload: Boolean(TaskDetail.questionCount) });
		taskDispatch({ type: "SET_SAFETY", payload: Boolean(TaskDetail.safetyCount) });
		taskDispatch({ type: "SET_DOCUMENTS", payload: Boolean(TaskDetail.documentCount) });
		taskDispatch({ type: "SET_PARTS", payload: Boolean(TaskDetail.partCount) });
	}, [TaskDetail]);

	// Memoized tab content with unique keys to force proper unmounting
	// This prevents detached DOM nodes from Material UI components
	const currentTabContent = useMemo(() => {
		switch (current) {
			case "Details":
				return (
					<Details
						key={`details-${taskInfo.id}`}
						state={siteAppState}
						taskInfo={taskInfo}
						access={access}
						setTaskInfo={setTaskInfo}
					/>
				);
			case "interval":
				return (
					<Intervals
						key={`interval-${taskInfo.id}`}
						taskInfo={taskInfo}
						access={access}
					/>
				);
			case "arrangement":
				return (
					<Arrangements
						key={`arrangement-${taskInfo.id}`}
						taskInfo={taskInfo}
						access={access}
					/>
				);
			case "stage":
				return (
					<Stages
						key={`stage-${taskInfo.id}`}
						taskInfo={taskInfo}
						access={access}
					/>
				);
			case "zone":
				return (
					<Zones
						key={`zone-${taskInfo.id}`}
						taskInfo={taskInfo}
						access={access}
					/>
				);
			case "part":
				return (
					<Parts
						key={`part-${taskInfo.id}`}
						taskInfo={taskInfo}
						access={access}
					/>
				);
			case "lubricant":
				return (
					<Lubricant
						key={`lubricant-${taskInfo.id}`}
						taskInfo={taskInfo}
						access={access}
					/>
				);
			case "tool":
				return (
					<Tools
						key={`tool-${taskInfo.id}`}
						taskInfo={taskInfo}
						access={access}
					/>
				);
			case "permit":
				return (
					<Permits
						key={`permit-${taskInfo.id}`}
						taskInfo={taskInfo}
						access={access}
					/>
				);
			case "workbook":
				return (
					<WorkBook
						key={`workbook-${taskInfo.id}`}
						taskInfo={taskInfo}
						access={access}
						setTaskInfo={setTaskInfo}
					/>
				);
			case "images":
				return (
					<Images
						key={`images-${taskInfo.id}`}
						taskInfo={taskInfo}
						access={access}
					/>
				);
			case "question":
				return (
					<Questions
						key={`question-${taskInfo.id}`}
						captions={{
							plural: customCaptions?.questionPlural,
							singular: customCaptions?.question,
							taskCaption: customCaptions?.task,
						}}
						service={customCaptions?.service}
						taskInfo={taskInfo}
						access={access}
						allowConditionMonitoringDataAlert={
							siteAppState?.data?.allowConditionMonitoringDataAlert
						}
					/>
				);
			case "attachment":
				return (
					<Attachments
						key={`attachment-${taskInfo.id}`}
						taskInfo={taskInfo}
						access={access}
					/>
				);
			default:
				return (
					<Details
						key={`details-default-${taskInfo.id}`}
						siteAppState={siteAppState}
						taskInfo={taskInfo}
						access={access}
						setTaskInfo={setTaskInfo}
					/>
				);
		}
	}, [
		current,
		taskInfo,
		access,
		siteAppState,
		setTaskInfo,
		customCaptions?.questionPlural,
		customCaptions?.question,
		customCaptions?.task,
		customCaptions?.service,
	]);

	//to show table-details only if the state is true for certain table-Header

	const modalNavigationData = [
		{ label: t("title.details"), name: "Details" },
		{
			label: `${customCaptions?.intervalPlural} (${
				TaskDetail?.intervalCount || 0
			})`,
			name: "interval",
			hasError: TaskDetail?.intervalCount === 0 || 0 ? true : false,
			errorMessage: t("message.noEntityAssigned", { entity: customCaptions?.intervalPlural }),
		},
		...(showArrangements && state?.modelDetail?.arrangementCount > 0
			? [
					{
						label: `${customCaptions?.arrangementPlural} (${
							TaskDetail?.arrangementCount || 0
						})`,
						name: "arrangement",
					},
			  ]
			: []),
		{
			label: `${customCaptions?.stagePlural} (${TaskDetail?.stageCount || 0})`,
			name: "stage",
			hasError: TaskDetail?.stageCount === 0 || 0 ? true : false,
			errorMessage: t("message.noEntityAssigned", { entity: customCaptions?.stagePlural }),
		},
		{
			label: `${customCaptions?.zonePlural} (${TaskDetail?.zoneCount || 0})`,
			name: "zone",
			hasError:
				stageList.filter((x) => x.hasZones && x.id !== null)?.length > 0 &&
				TaskDetail?.zoneCount === 0 &&
				TaskDetail?.stageCount !== 0
					? true
					: false,
			errorMessage: t("message.noEntityAssigned", { entity: customCaptions?.zonePlural }),
		},
		...(showLubricants
			? [
					{
						label: `${customCaptions?.lubricant}`,
						name: "lubricant",
					},
			  ]
			: []),
		...(showParts
			? [
					{
						label: `${customCaptions?.partPlural} (${
							TaskDetail?.partCount || 0
						})`,
						name: "part",
					},
			  ]
			: []),

		{
			label: `${customCaptions?.toolPlural} (${TaskDetail?.toolCount || 0})`,
			name: "tool",
		},
		{
			label: `${customCaptions?.permitPlural}  (${
				TaskDetail?.permitCount || 0
			})`,
			name: "permit",
		},
		{ label: `${customCaptions?.workbook}`, name: "workbook" },
		{
			label: t("label.imagesCount", { count: TaskDetail?.imageCount || 0 }),
			name: "images",
		},
		{
			label: `${customCaptions?.questionPlural} (${
				TaskDetail?.questionCount || 0
			})`,
			name: "question",
		},
		{
			label: t("label.attachmentsCount", {
				count: TaskDetail?.documentCount || 0,
			}),
			name: "attachment",
		},
	];

	return (
		<div className={classes.main}>
			{taskLoading ? (
				<LinearProgress />
			) : (
				<div className="task-expand-table">
					<Navigation
						current={current}
						navigation={modalNavigationData}
						onClick={(d) => setCurrent(d)}
					/>
					<div
						key={`tab-wrapper-${current}-${taskInfo.id}`}
						className={classes.component}
					>
						{currentTabContent}
					</div>
				</div>
			)}
		</div>
	);
};

export default ModelTaskExpand;
